import { isLocalHost } from '~/utils/logging/host'
import { isIgnorableClientReport, statusCodeOf } from '~/utils/logging/clientNoise'

type ClientErrorPayload = {
  name: string
  message: string
  stack?: string | null
  level?: string
  component?: string | null
  route?: string | null
  path?: string | null
  requestId?: string | null
  context?: Record<string, unknown>
  occurredAt?: string
}

type Breadcrumb = {
  type: string
  message: string
  ts: number
  data?: Record<string, unknown>
}

const MAX_QUEUE = 20
const MAX_BREADCRUMBS = 30
const FLUSH_MS = 2500
const MAX_PER_MINUTE = 30

export default defineNuxtPlugin((nuxtApp) => {
  if (import.meta.server) return

  const config = useRuntimeConfig()
  if ((config.public as { errorLogEnabled?: boolean }).errorLogEnabled === false) return

  // Never ship client error batches from localhost / loopback
  if (typeof window !== 'undefined' && isLocalHost(window.location.host)) return

  const queue: ClientErrorPayload[] = []
  const breadcrumbs: Breadcrumb[] = []
  let flushTimer: ReturnType<typeof setTimeout> | undefined
  let sentThisMinute = 0
  let minuteStartedAt = Date.now()
  let currentPath = typeof window !== 'undefined' ? window.location.pathname : '/'
  let currentRoute = currentPath
  const originalConsoleError = console.error.bind(console)

  try {
    const route = useRoute()
    currentPath = route.path
    currentRoute = route.fullPath
  }
  catch { /* ignore */ }

  function pushBreadcrumb(entry: Breadcrumb) {
    breadcrumbs.push(entry)
    if (breadcrumbs.length > MAX_BREADCRUMBS) breadcrumbs.shift()
  }

  function canSend(): boolean {
    const now = Date.now()
    if (now - minuteStartedAt > 60_000) {
      minuteStartedAt = now
      sentThisMinute = 0
    }
    if (sentThisMinute >= MAX_PER_MINUTE) return false
    return true
  }

  function enqueue(payload: ClientErrorPayload) {
    if (!canSend()) return
    if (isIgnorableClientReport({
      message: payload.message,
      stack: payload.stack,
      statusCode: statusCodeOf(payload),
    })) return
    // Avoid logging our own ingest failures
    if (payload.path?.includes('/api/_log') || payload.message?.includes('/api/_log/client')) return

    queue.push({
      ...payload,
      context: {
        ...(payload.context || {}),
        breadcrumbs: breadcrumbs.slice(-20),
      },
      occurredAt: payload.occurredAt || new Date().toISOString(),
    })
    if (queue.length > MAX_QUEUE) queue.shift()
    scheduleFlush()
  }

  function scheduleFlush() {
    if (flushTimer) return
    flushTimer = setTimeout(() => {
      flushTimer = undefined
      void flush()
    }, FLUSH_MS)
  }

  async function flush() {
    if (!queue.length || !canSend()) return
    const batch = queue.splice(0, MAX_QUEUE)
    sentThisMinute += batch.length
    const body = JSON.stringify({ events: batch })
    const url = '/api/_log/client'
    try {
      if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
        const blob = new Blob([body], { type: 'application/json' })
        const ok = navigator.sendBeacon(url, blob)
        if (ok) return
      }
      await fetch(url, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body,
        keepalive: true,
        credentials: 'same-origin',
      })
    }
    catch {
      // drop on failure — never throw into the app
    }
  }

  function fromUnknown(error: unknown, extra?: Partial<ClientErrorPayload>): ClientErrorPayload {
    let name = 'Error'
    let message = 'Unknown error'
    let stack: string | null = null
    if (error instanceof Error) {
      name = error.name || 'Error'
      message = error.message || name
      stack = error.stack || null
    }
    else if (typeof error === 'string') {
      message = error
    }
    else if (error && typeof error === 'object') {
      const e = error as { name?: string; message?: string; stack?: string; statusMessage?: string }
      name = e.name || 'Error'
      message = e.message || e.statusMessage || name
      stack = e.stack || null
    }
    return {
      name,
      message: String(message).slice(0, 2000),
      stack,
      level: 'error',
      path: currentPath || (typeof window !== 'undefined' ? window.location.pathname : null),
      route: currentRoute || null,
      ...extra,
    }
  }

  function report(error: unknown, extra?: Partial<ClientErrorPayload>) {
    if (isIgnorableClientReport({
      message: error instanceof Error ? error.message : typeof error === 'string' ? error : undefined,
      stack: error instanceof Error ? error.stack : null,
      statusCode: statusCodeOf(error),
    })) return
    enqueue(fromUnknown(error, extra))
  }

  nuxtApp.hook('vue:error', (error, instance, info) => {
    const component = instance?.$options?.name
      || (instance?.type as { name?: string; __name?: string } | undefined)?.name
      || (instance?.type as { __name?: string } | undefined)?.__name
      || null
    report(error, {
      component,
      context: { vueInfo: String(info || '') },
    })
  })

  nuxtApp.hook('app:error', (error) => {
    report(error, { level: 'error' })
  })

  window.addEventListener('error', (event) => {
    report(event.error || event.message, {
      context: {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      },
    })
  })

  window.addEventListener('unhandledrejection', (event) => {
    report(event.reason, { level: 'error', context: { type: 'unhandledrejection' } })
  })

  console.error = (...args: unknown[]) => {
    try {
      originalConsoleError(...args)
    }
    catch { /* ignore */ }
    try {
      const first = args[0]
      const message = args.map((a) => {
        if (typeof a === 'string') return a
        if (a instanceof Error) return a.message
        try { return JSON.stringify(a) }
        catch { return String(a) }
      }).join(' ').slice(0, 2000)
      report(first instanceof Error ? first : message, {
        level: 'error',
        context: { via: 'console.error', argc: args.length },
      })
    }
    catch { /* never break console */ }
  }

  const router = useRouter()
  router.afterEach((to, from) => {
    currentPath = to.path
    currentRoute = to.fullPath
    pushBreadcrumb({
      type: 'navigation',
      message: `${from.fullPath} → ${to.fullPath}`,
      ts: Date.now(),
    })
  })

  document.addEventListener('click', (event) => {
    const target = event.target as HTMLElement | null
    if (!target) return
    const label = (target.closest('[data-testid],button,a') as HTMLElement | null)?.tagName
      || target.tagName
    pushBreadcrumb({
      type: 'click',
      message: String(label || 'click'),
      ts: Date.now(),
      data: { id: target.id || undefined },
    })
  }, { capture: true, passive: true })

  const originalFetch = window.fetch.bind(window)
  window.fetch = async (...args: Parameters<typeof fetch>) => {
    try {
      const res = await originalFetch(...args)
      if (res.status >= 500) {
        const url = typeof args[0] === 'string' ? args[0] : (args[0] as Request)?.url
        pushBreadcrumb({
          type: 'http',
          message: `HTTP ${res.status}`,
          ts: Date.now(),
          data: { url: String(url || '').slice(0, 200), status: res.status },
        })
      }
      return res
    }
    catch (error) {
      const url = typeof args[0] === 'string' ? args[0] : (args[0] as Request)?.url
      pushBreadcrumb({
        type: 'http',
        message: 'fetch failed',
        ts: Date.now(),
        data: { url: String(url || '').slice(0, 200) },
      })
      throw error
    }
  }

  window.addEventListener('pagehide', () => { void flush() })
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') void flush()
  })
})
